import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "@lib/i18n/navigation-pages-router";
import {
  useUserAddresses,
  useAddAddress,
  useUpdateAddressType,
  useDeliveryPickupCarriersFor,
} from "@hooks/use-addresses";
import { useOpenDrawer } from "@hooks/use-drawer";
import { showSuccessToast, showErrorToast } from "@utils/toasts/showToasts";
import { useUserShippingAddress } from "@hooks/use-addresses";
import { Button } from "@components/core";
import SharedAccountsLayout from "@components/core/SharedAccountsLayout";
import deliveryModes from "@utils/constants/deliveryModes";
import addressTypes from "@utils/constants/addressTypes";
import carriers from "@utils/constants/carriers";
import DeliveryModeChoice from "./DeliveryModeChoice";
import RelayPointsListMap from "@components/core/RelayPointsMap/RelayPointsListMap";
import HomeAddressChoice from "../HomeAddressChoice";

const Shipping = () => {
  const t = useTranslations("addresses");

  const router = useRouter();
  const openDrawer = useOpenDrawer();

  const userAddresses = useUserAddresses();
  const addAddress = useAddAddress();
  const updateAddressType = useUpdateAddressType();
  const currentAddress = useUserShippingAddress();
  const currentAddressId = currentAddress?.id;
  const deliveryPickupCarriers = useDeliveryPickupCarriersFor(currentAddress?.country);

  const [currentMode, setCurrentMode] = useState(currentAddress?.delivery_mode || "home");
  const [focusedAddressId, setFocusedAddressId] = useState(currentAddress?.id);
  const [addressSelectionMode, setAddressSelectionMode] = useState(
    deliveryPickupCarriers?.length === 0 ? "home" : null,
  );

  const handleShippingAddress = (e) => {
    setFocusedAddressId(e.target.value);
  };

  const addAddressHref = () => {
    const redirect = router.query?.redirect ? `&redirect=${router.query.redirect}` : "";
    const origin = router.query?.origin ? `&origin=${router.query.origin}` : "";
    return `/profile/addresses/new?address_type=delivery${redirect}${origin}`;
  };

  const handleDeliveryModeChoice = (e) => {
    setCurrentMode(e.target.value);

    if (e.target.value === currentAddress?.delivery_mode) {
      setAddressSelectionMode(null);
    } else {
      setAddressSelectionMode(e.target.value);
    }
  };

  const handleAddressSubmit = async (addressId) => {
    try {
      await updateAddressType(addressId, addressTypes.shipping);

      if (router.query?.redirect) {
        router.replace(router.query.redirect).then(() => {
          showSuccessToast(
            t("choose_home_address.success.title"),
            t("choose_home_address.success.content.shipping"),
          );
          if (router.query.origin === "closetDrawer") openDrawer();
        });
      } else {
        router.back();
        setTimeout(() => {
          showSuccessToast(
            t("choose_home_address.success.title"),
            t("choose_home_address.success.content.shipping"),
          );
        }, 100);
      }
    } catch (err) {
      showErrorToast(t("error_msg_default.title"), t("error_msg_default.content"));
    }
  };

  function pickupTypeRelayPoint(pickupType) {
    switch (pickupType) {
      case "MRP":
        return carriers.mondialRelay;
      case "BPR":
      case "A2P":
      case "CMT":
      case "BDP":
        return carriers.colissimo;
      case "CPP":
      case "PUDO":
      case "LOCKER":
        return carriers.colisPrive;
      default:
        return "";
    }
  }

  const handleRelayPointSubmit = async (relayPoint) => {
    const data = {
      address_type: { delivery: true, collection: false, billing: false },
      carrier: pickupTypeRelayPoint(relayPoint.pickup_type),
      delivery_mode: deliveryModes.pickup,
      data: relayPoint,
    };

    addShippingAddress(data);
  };

  async function addShippingAddress(data) {
    try {
      await addAddress(data);

      if (router.query?.redirect) {
        router.replace(router.query.redirect).then(() => {
          showSuccessToast(
            t("choose_relay_point.success.title"),
            t("choose_relay_point.success.content"),
          );
          if (router.query.origin === "closetDrawer") openDrawer();
        });
      } else {
        router.back();
        setTimeout(() => {
          showSuccessToast(
            t("choose_relay_point.success.title"),
            t("choose_relay_point.success.content"),
          );
        }, 100);
      }
    } catch (err) {
      showErrorToast(t("error_msg_default.title"), t("error_msg_default.content"));
    }
  }

  useEffect(() => {
    async function fetchInitData() {
      setCurrentMode(currentAddress?.delivery_mode || "home");
      setFocusedAddressId(currentAddressId);
    }

    fetchInitData();
  }, [userAddresses]);

  return (
    <>
      <SharedAccountsLayout title="shipping_address_title" />
      <div className="container max-accounts-layout-size">
        {deliveryPickupCarriers?.length > 0 && (
          <DeliveryModeChoice
            onChange={handleDeliveryModeChoice}
            onChangeAddress={() => setAddressSelectionMode("home")}
            onChangeRelayPoint={() => setAddressSelectionMode("pickup")}
            currentMode={currentMode}
            currentAddress={currentAddress}
            hideChangeAddress={addressSelectionMode == "home"}
            hideChangeRelayPoint={addressSelectionMode == "pickup"}
          />
        )}

        {addressSelectionMode == "home" && (
          <HomeAddressChoice
            title={t("shipping_form.select_delivery_address")}
            addAddressLink={addAddressHref()}
            onChange={handleShippingAddress}
            focusedAddressId={focusedAddressId}
          />
        )}

        <div hidden={addressSelectionMode !== "pickup"}>
          <h6 className="mt-4">{t("shipping_form.select_relay_point")}</h6>
          <RelayPointsListMap
            startingAddress={currentAddress}
            carriersToDisplay={deliveryPickupCarriers}
            withSubmitButtons
            onRelayPointSubmit={handleRelayPointSubmit}
          />
        </div>
      </div>

      {currentMode == "home" && (
        <div
          style={{ position: "sticky", bottom: 0 }}
          className="w-100 p-3 mb-3 text-center bg-base"
        >
          <Button
            variant="primary"
            onClick={() => handleAddressSubmit(focusedAddressId)}
          >
            {t("shipping_form.validate_delivery_address")}
          </Button>
        </div>
      )}
    </>
  );
};

export default Shipping;
